import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAnchorRect } from "hooks/useAnchorRect";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Check, Undo2 } from "lucide-react";
import RelativeTime from "components/RelativeTime";
import Skeleton from "components/Skeleton";
import {
  useUnreadCount,
  useNotifications,
  useMarkRead,
  useMarkUnread,
  useMarkAllRead,
} from "hooks/crm/useNotifications";

/**
 * NotificationBell (SOUND-515) — the top-bar in-app notification center.
 * Badge polls the unread count (~60s + on focus); the panel lazy-loads the
 * inbox on open. A notification deep-links via its `entityRef` (a small
 * type→route map, extendable; unknown types just mark-read, no nav). Rendered
 * internal-first (gated in AppTopBar). Ticketing/elevated design bar.
 */

// entityRef.type → route. Extend as more entity types emit notifications.
const ENTITY_ROUTES = {
  ticket: (id) => `/tickets/${id}`,
};
function entityHref(ref) {
  if (!ref || !ref.type || !ref.id) return null;
  const fn = ENTITY_ROUTES[ref.type];
  return fn ? fn(ref.id) : null;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  // SOUND-616/617 — the panel is PORTALED to document.body (see the render
  // below), so it needs its own ref: it is no longer a DOM descendant of
  // rootRef, and the click-outside check has to consider both.
  const panelRef = useRef(null);
  // Shared with ThemeDropdown — same stacking-context problem, same fix.
  const anchor = useAnchorRect(open, rootRef);

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data, isLoading } = useNotifications({ limit: 15, enabled: open });
  const markRead = useMarkRead();
  const markUnread = useMarkUnread();
  const markAllRead = useMarkAllRead();

  const notifications = data?.data || [];

  useEffect(() => {
    if (!open) return;
    // The panel lives OUTSIDE rootRef now (portaled to body), so a naive
    // `!rootRef.contains(target)` treats every click inside the panel as an
    // outside click and closes it instantly — which would break the SOUND-521
    // read/unread toggle and click-to-open. "Outside" means outside BOTH.
    const onDoc = (e) => {
      const inButton = rootRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inButton && !inPanel) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);


  const openNotification = (n) => {
    if (!n.readAt) markRead.mutate(n.notificationId);
    const href = entityHref(n.entityRef);
    setOpen(false);
    if (href) navigate(href);
  };

  // SOUND-521 — explicit read/unread toggle, deliberately SEPARATE from
  // openNotification (which marks read + navigates). Opening a notification in
  // passing used to destroy it as a work signal with no way back; this is the way
  // back. The panel stays open: the user is managing state, not consuming it.
  //
  // The list re-sorts on success because the backend orders unread-first, so a
  // row marked unread jumps to the top of the panel. That is the documented
  // ordering, not a bug — and the row stays on screen, so it reads as a move
  // rather than a disappearance.
  const busy = markRead.isLoading || markUnread.isLoading;
  const toggleRead = (n) => {
    if (n.readAt) markUnread.mutate(n.notificationId);
    else markRead.mutate(n.notificationId);
  };

  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid min-w-[16px] place-items-center rounded-full bg-brand px-1 text-[10px] font-bold leading-4 text-brand-foreground">
            {badge}
          </span>
        )}
      </button>

      {/* SOUND-616/617 — PORTALED to document.body.
          The panel used to be an absolute child of this component, so its z-50
          was trapped inside the AccountLayout wrapper's `sticky z-20` stacking
          context. PageLayout's sticky header (`z-30` + backdrop-blur) is a
          SIBLING of that wrapper, so it painted over the dropdown — visible as a
          blur across the top of the list on any page with a sticky header
          (/tickets, /mids) and fine everywhere else.
          Bumping a z-index would only move the problem: SOUND-239 fixed the same
          class of bug once already. A portal to body escapes EVERY stacking
          context, so no future sticky/blur/z-index can cover it. */}
      {open &&
        anchor &&
        createPortal(
          <div
            ref={panelRef}
            style={{ top: anchor.top, right: anchor.right }}
            className="fixed z-[60] flex max-h-[70vh] w-80 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-lg sm:w-96">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">Notifications</span>
              {unreadCount > 0 && <span className="badge badge-brand">{unreadCount}</span>}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isLoading}
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="space-y-3 p-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex gap-3">
                    <Skeleton.Circle size={8} className="mt-1.5" />
                    <div className="flex-1">
                      <Skeleton.Text lines={2} />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-2 text-sm font-medium text-foreground">You're all caught up</p>
                <p className="text-xs text-muted-foreground">New notifications land here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {notifications.map((n) => {
                  const unread = !n.readAt;
                  const href = entityHref(n.entityRef);
                  return (
                    // SOUND-521: the row used to be ONE full-width button. The
                    // read/unread toggle cannot nest inside it — nested
                    // interactive elements are invalid HTML and the click would
                    // bubble into openNotification, navigating away while
                    // appearing to toggle. So the row is now a flex <li> holding
                    // two SIBLING buttons; the hover/unread tint moved to the
                    // <li> so the whole row still highlights as one unit.
                    <li
                      key={n.notificationId}
                      className={`group flex items-start transition-colors hover:bg-muted ${
                        unread ? "bg-brand/[0.05]" : ""
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => openNotification(n)}
                        className="flex min-w-0 flex-1 gap-3 py-3 pl-4 pr-2 text-left"
                      >
                        <span className="mt-1.5 shrink-0">
                          {unread ? (
                            <span className="block h-2 w-2 rounded-full bg-brand" />
                          ) : (
                            <span className="block h-2 w-2 rounded-full border border-border" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block text-sm ${
                              unread ? "font-semibold text-foreground" : "font-medium text-foreground/90"
                            }`}
                          >
                            {n.title}
                          </span>
                          {n.body && (
                            <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                              {n.body}
                            </span>
                          )}
                          <span className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                            <RelativeTime value={n.createdAt} />
                            {href && <span className="text-brand">· Open</span>}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleRead(n)}
                        disabled={busy}
                        aria-label={unread ? "Mark as read" : "Mark as unread"}
                        title={unread ? "Mark as read" : "Mark as unread"}
                        className="mr-2 mt-3 shrink-0 rounded p-1.5 text-muted-foreground opacity-70 transition hover:bg-background hover:text-foreground focus-visible:opacity-100 disabled:opacity-30 group-hover:opacity-100"
                      >
                        {unread ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Undo2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>,
          document.body
        )}
    </div>
  );
}
